<!-- 个人电站列表 -->
<template>
  <div class="personal-stations">
    <v-card class="ps-frame rounded-xl pa-4 d-flex flex-column" variant="flat">
      <!-- 顶部工具栏 -->
      <div class="toolbar-top mb-4 flex-shrink-0">
        <div class="ps-title-row d-flex align-center ga-2">
          <v-btn icon="mdi-arrow-left" rounded="circle" variant="flat" size="small" @click="router.back()"></v-btn>
          <div class="ps-section-title mb-0">我创建的电站</div>
        </div>
        <div class="toolbar-main d-flex flex-wrap align-center ga-4">
          <div class="search-item">
            <span class="search-label">电站名称</span>
            <v-text-field v-model="filterKeyword" placeholder="请输入电站名称" variant="outlined" density="compact"
              hide-details clearable rounded="xl" prepend-inner-icon="mdi-magnify" class="search-input"
              @keyup.enter="onSearch" />
          </div>
          <div class="search-item">
            <span class="search-label">审批状态</span>
            <v-select v-model="filterApproveStatus" :items="APPROVE_STATUS_ITEMS" item-title="title" item-value="value"
              placeholder="全部状态" variant="outlined" density="compact" hide-details clearable rounded="xl"
              class="search-input--select" />
          </div>
          <div class="search-actions d-flex ga-3 ml-auto">
            <v-btn color="primary" rounded="xl" class="action-btn" @click="onSearch">
              <v-icon start icon="mdi-magnify" />查询
            </v-btn>
            <v-btn variant="outlined" color="success" rounded="xl" class="action-btn" @click="resetFilters">
              <v-icon start icon="mdi-refresh" />重置
            </v-btn>
          </div>
        </div>
      </div>

      <!-- 表格 -->
      <div class="table-wrap">
        <div v-if="pagedList.length" class="ps-table-host">
          <v-table fixed-header height="100%" class="gm-table ps-table gm-border rounded-lg" density="comfortable">
            <thead>
              <tr>
                <th style="min-width: 200px">电站名称</th>
                <th style="min-width: 160px">型号 / 容量</th>
                <th style="min-width: 160px">区域 / 地址</th>
                <th style="min-width: 90px">申请者</th>
                <th style="min-width: 90px">审批人</th>
                <th style="min-width: 120px">审批状态</th>
                <th style="min-width: 170px">创建时间</th>
                <th style="min-width: 170px">审批 / 驳回时间</th>
                <th style="min-width: 110px" class="text-center">配置进度</th>
                <th style="width: 280px; min-width: 280px" class="text-center sticky-action-col">操作</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in pagedList" :key="item.stationId">
                <tr class="ps-table-row">
                  <!-- 名称 + ID -->
                  <td>
                    <div class="cell-name" :title="item.name">{{ item.name }}</div>
                    <div class="text-caption text-medium-emphasis">ID：{{ item.stationId }}</div>
                  </td>

                  <!-- 型号 + 容量 -->
                  <td>
                    <div class="ps-text" :title="item.model">{{ item.model }}</div>
                    <div class="ps-sub ps-link-color">{{ item.capacity }}</div>
                  </td>

                  <!-- 区域 + 地址 -->
                  <td>
                    <div class="ps-text">{{ item.region }}</div>
                    <div class="ps-sub ps-link-color" :title="item.address">{{ item.address }}</div>
                  </td>

                  <!-- 申请者 -->
                  <td>{{ item.applicantName || '--' }}</td>

                  <!-- 审批人 -->
                  <td>{{ item.approverName || '--' }}</td>

                  <!-- 审批状态 -->
                  <td>
                    <div v-if="item.approveStatus" class="ps-status">
                      <v-chip :color="displayStatus(item).color" variant="tonal" size="small" label
                        class="ps-status-chip">{{ displayStatus(item).text }}</v-chip>
                      <!-- 已驳回：点击留言图标弹出驳回留言 -->
                      <v-menu v-if="item.approveStatus === 'rejected'" :close-on-content-click="false" location="bottom"
                        offset="6">
                        <template #activator="{ props }">
                          <button type="button" class="ps-status-icon-btn" v-bind="props"
                            :style="{ color: displayStatus(item).color }" title="查看驳回留言" @click.stop>
                            <svg class="ps-status-icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                              <path fill="currentColor"
                                d="M3 2.5h10A1.5 1.5 0 0 1 14.5 4v6A1.5 1.5 0 0 1 13 11.5H6.8L4 13.8V11.5H3A1.5 1.5 0 0 1 1.5 10V4A1.5 1.5 0 0 1 3 2.5Z" />
                              <path fill="#fff" d="M5 5.4h6v1H5zM5 7.4h4v1H5z" />
                            </svg>
                          </button>
                        </template>
                        <div class="ps-reject-pop" @click.stop>
                          <div class="ps-reject-pop-title">驳回留言</div>
                          <template v-if="item.rejectMessages && item.rejectMessages.length">
                            <div v-for="(msg, mi) in item.rejectMessages" :key="mi" class="ps-reject-msg">
                              <div class="ps-reject-msg-head">
                                <span class="ps-reject-approver">{{ msg.approver }}</span>
                                <span class="ps-reject-time">{{ msg.time }}</span>
                              </div>
                              <div class="ps-reject-content">{{ msg.content }}</div>
                            </div>
                          </template>
                          <div v-else class="ps-reject-empty">暂无留言</div>
                        </div>
                      </v-menu>
                    </div>
                    <span v-else>--</span>
                  </td>

                  <!-- 创建时间 -->
                  <td class="ps-muted">{{ item.createTime || '—' }}</td>

                  <!-- 审批/驳回时间 -->
                  <td class="ps-muted">{{ item.approveTime || '--' }}</td>

                  <!-- 配置进度 -->
                  <td class="text-center">
                    <div class="ps-progress-cell">
                      <span class="ps-pie" :title="`已配置 ${configuredCount(item)}/${item.steps.length}`">
                        <svg viewBox="0 0 24 24" width="22" height="22">
                          <circle cx="12" cy="12" r="11" :fill="pieTrackColor(item)" />
                          <path v-if="pieMode(item) === 'arc'" :d="piePath(configuredRatio(item))"
                            :fill="PROGRESS_GREEN" />
                          <!-- 已投运：对勾 -->
                          <path v-if="pieMode(item) === 'check'" d="M7.2 12.2l3 3 6-6.4" fill="none" stroke="#fff"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          <!-- 已驳回：省略号 -->
                          <g v-else-if="pieMode(item) === 'dots'" fill="#fff">
                            <circle cx="8" cy="12" r="1.3" />
                            <circle cx="12" cy="12" r="1.3" />
                            <circle cx="16" cy="12" r="1.3" />
                          </g>
                        </svg>
                      </span>
                      <span class="ps-progress-text">{{ configuredCount(item) }}/{{ item.steps.length }}</span>
                    </div>
                  </td>

                  <!-- 操作 -->
                  <td class="text-center sticky-action-col">
                    <div class="action-buttons justify-center">
                      <!-- 审批记录：弹出时间线 -->
                      <v-menu :close-on-content-click="false" location="bottom" offset="6"
                        @update:modelValue="(open) => onApproveRecordMenu(open, item)">
                        <template #activator="{ props }">
                          <v-btn variant="text" color="#2655f1" rounded="xl" density="comfortable"
                            v-bind="props">审批记录</v-btn>
                        </template>
                        <ApproveRecordTimeline :records="getApproveRecords(item)"
                          :loading="isApproveRecordsLoading(item)" />
                      </v-menu>
                      <v-btn variant="text" color="#2655f1" rounded="xl" density="comfortable"
                        @click="handleEdit(item)">详情</v-btn>
                      <v-btn variant="text" color="#2655f1" rounded="xl" density="comfortable"
                        @click="toggleExpand(item.stationId)">
                        {{ expandedIds.includes(item.stationId) ? '收起' : '配置' }}
                        <v-icon end size="18"
                          :icon="expandedIds.includes(item.stationId) ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <!-- 展开行：建站配置流程 -->
                <tr v-if="expandedIds.includes(item.stationId)" class="ps-expand-row">
                  <td :colspan="10" class="ps-expand-cell">
                    <div class="ps-steps">
                      <template v-for="(step, index) in item.steps" :key="step.label">
                        <!-- 连接线（节点之间） -->
                        <span v-if="index > 0" class="ps-step-line"
                          :style="{ background: step.configured ? PROGRESS_GREEN : PROGRESS_PINK }" />
                        <div class="ps-step">
                          <span class="ps-step-dot"
                            :style="{ background: step.configured ? PROGRESS_GREEN : PROGRESS_PINK }">{{ index + 1
                            }}</span>
                          <span class="ps-step-label">{{ step.label }}</span>
                          <span class="ps-step-state">{{ step.configured ? '已配置' : '未配置' }}</span>
                        </div>
                      </template>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </div>
        <div v-else class="empty-tip text-center py-10 text-medium-emphasis">
          暂无符合条件的电站
        </div>
      </div>

      <!-- 分页 -->
      <TablePagination v-if="pagedList.length > 0" v-model="pageNum" v-model:pageSize="pageSize" :total="total" hasSizes
        hasJump color="#2655f1" class="ps-pagination flex-shrink-0" />
    </v-card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import TablePagination from '@/components/TablePagination/index.vue'
import ApproveRecordTimeline from './ApproveRecordTimeline.vue'
import { getMyStationApprovalList, getStationApprovalList } from '@/api/device/station'

const router = useRouter()

const fmtTime = (v) => {
  if (!v) return ''
  const d = dayjs(v)
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(v)
}

/** 进度色板：绿=已配置，粉=未配置 */
const PROGRESS_GREEN = '#1FC76B'
const PROGRESS_PINK = '#F0397B'

/** 审批状态枚举（UI 值 ↔ 接口 status：0待审批 1通过 2驳回） */
const APPROVE_STATUS_ITEMS = [
  { title: '审批中', value: 'pending' },
  { title: '审批通过', value: 'approved' },
  { title: '已驳回', value: 'rejected' },
]

const STATUS_CODE_TO_KEY = { 0: 'pending', 1: 'approved', 2: 'rejected' }
const STATUS_KEY_TO_CODE = { pending: 0, approved: 1, rejected: 2 }

/**
 * 卡片状态文字派生规则：
 */
const displayStatus = (item) => {
  if (item.approveStatus === 'pending') return { text: '审批中', color: '#1FC76B' }
  if (item.approveStatus === 'rejected') return { text: '已驳回', color: '#FF8A1E' }
  if (item.approveStatus === 'approved') return { text: '审批通过', color: '#2655f1' }
  return { text: '--', color: '#909399' }
}

/** 建站流程（3 步：基础信息 / 设备 / 拓扑），根据接口 configStep 计算已配置数量
 *  configStep 即已配置步数：1→已配基础 / 2→已配基础+设备 / 3→三步全配 / 4→完成态
 */
const STEP_LABELS = ['基本信息', '电站设备', '电站拓扑']
const buildSteps = (configStep) => {
  const done = Math.min(STEP_LABELS.length, Math.max(0, configStep || 0))
  return STEP_LABELS.map((label, i) => ({ label, configured: i < done }))
}

/** 地址：省 + 市 + 区县 拼接（空字段自动跳过） */
const composeAddress = (row) =>
  [row.province, row.city, row.district].filter(Boolean).join('') || '—'

/** 容量 / 功率展示：接口 capacity 单位 kWh、maxPower 单位 kW，拼成 “xx kW / xx kWh” */
const composeCapacity = (row) => {
  const power = row.maxPower ? `${row.maxPower}kW` : ''
  const cap = row.capacity ? `${row.capacity}kWh` : ''
  if (power && cap) return `${power}/${cap}`
  return power || cap || '—'
}

/** 由「电站建站审核列表」多条记录构造时间线（按 applyTime 升序，逐条展开为 submit + 结果） */
const buildApproveRecordsFromList = (rows = []) => {
  const list = []
  const sorted = [...rows].sort((a, b) => {
    const ta = new Date(a.applyTime || a.createTime || 0).getTime()
    const tb = new Date(b.applyTime || b.createTime || 0).getTime()
    return ta - tb
  })
  for (const row of sorted) {
    if (row.applyTime || row.applicantName) {
      list.push({
        action: 'submit',
        operator: row.applicantName || row.applicantAccount || '—',
        time: fmtTime(row.applyTime),
        comment: '提交建站审批申请',
      })
    }
    if (row.status === 1 && (row.approveTime || row.approverName)) {
      list.push({
        action: 'approve',
        operator: row.approverName || row.approverAccount || '—',
        time: fmtTime(row.approveTime),
        comment: row.approveRemark || '审核通过',
      })
    } else if (row.status === 2 && (row.approveTime || row.approverName)) {
      list.push({
        action: 'reject',
        operator: row.approverName || row.approverAccount || '—',
        time: fmtTime(row.approveTime),
        comment: row.approveRemark || '',
      })
    }
  }
  return list
}

/** 接口行 → 页面展示行 */
const mapRow = (row) => {
  const approveStatus = STATUS_CODE_TO_KEY[row.status]
  return {
    id: row.id,
    name: row.stationName || '—',
    stationId: row.stationId ?? row.bizId ?? '—',
    model: row.productModel || '—',
    capacity: composeCapacity(row),
    region: row.area || '—',
    address: composeAddress(row),
    owner: row.approverName || '--',
    applicantName: row.applicantName || '',
    approverName: row.approverName || '',
    approveStatus,
    createTime: fmtTime(row.createTime),
    approveTime: fmtTime(row.approveTime),
    steps: buildSteps(row.configStep),
    configStep: row.configStep,
    rejectMessages:
      approveStatus === 'rejected' && row.approveRemark
        ? [{ approver: row.approverName || '—', time: fmtTime(row.approveTime), content: row.approveRemark }]
        : [],
    _raw: row,
  }
}

/** 列表数据 */
const stationList = ref([])
const loading = ref(false)
const total = ref(0)

/** 筛选条件 */
const filterKeyword = ref('')
const filterApproveStatus = ref(null)

/** 分页 */
const pageNum = ref(1)
const pageSize = ref(10)

/** 拉取列表 */
const fetchList = async () => {
  loading.value = true
  try {
    const params = {
      pageNum: pageNum.value,
      pageSize: pageSize.value,
    }
    const kw = filterKeyword.value?.trim()
    if (kw) params.stationName = kw
    if (filterApproveStatus.value != null) {
      params.status = STATUS_KEY_TO_CODE[filterApproveStatus.value]
    }
    const res = await getMyStationApprovalList(params)
    const rows = res?.rows || res?.data?.rows || []
    stationList.value = rows.map(mapRow)
    total.value = res?.total ?? res?.data?.total ?? 0
  } catch (e) {
    console.error('获取我创建的电站列表失败', e)
    stationList.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

const pagedList = computed(() => stationList.value)

watch(pageNum, () => fetchList())
watch(pageSize, () => {
  pageNum.value = 1
  fetchList()
})

const onSearch = () => {
  pageNum.value = 1
  fetchList()
}

const resetFilters = () => {
  filterKeyword.value = ''
  filterApproveStatus.value = null
  pageNum.value = 1
  fetchList()
}

/** 审批记录懒加载：按 stationId 缓存时间线，避免重复请求 */
const approveRecordsMap = ref({}) // { [stationId]: TimelineRecord[] }
const approveRecordsLoadingMap = ref({}) // { [stationId]: boolean }

const loadApproveRecords = async (item) => {
  const key = item.stationId
  if (!key || key === '—') return
  // 已有缓存或正在加载 → 直接跳过
  if (approveRecordsMap.value[key] || approveRecordsLoadingMap.value[key]) return
  approveRecordsLoadingMap.value = { ...approveRecordsLoadingMap.value, [key]: true }
  try {
    const res = await getStationApprovalList({
      stationId: key,
      pageNum: 1,
      pageSize: 50,
    })
    const rows = res?.rows || []
    approveRecordsMap.value = {
      ...approveRecordsMap.value,
      [key]: buildApproveRecordsFromList(rows),
    }
  } catch (e) {
    console.error('获取审批记录失败', e)
    approveRecordsMap.value = { ...approveRecordsMap.value, [key]: [] }
  } finally {
    const next = { ...approveRecordsLoadingMap.value }
    delete next[key]
    approveRecordsLoadingMap.value = next
  }
}

const getApproveRecords = (item) => approveRecordsMap.value[item.stationId] || []
const isApproveRecordsLoading = (item) => !!approveRecordsLoadingMap.value[item.stationId]

/** 打开审批记录菜单时按需拉取 */
const onApproveRecordMenu = (open, item) => {
  if (open) loadApproveRecords(item)
}

onMounted(fetchList)

const expandedIds = ref([])

const toggleExpand = (id) => {
  const idx = expandedIds.value.indexOf(id)
  if (idx > -1) expandedIds.value.splice(idx, 1)
  else expandedIds.value.push(id)
}

const configuredCount = (item) => item.steps.filter((s) => s.configured).length
const configuredRatio = (item) => configuredCount(item) / item.steps.length

/**
 * 进度环形态：
 * 已驳回 → 省略号；已投运 → 对勾；其余 → 配置进度扇形
 */
const pieMode = (item) => {
  if (item.approveStatus === 'rejected') return 'dots'
  if (item.approveStatus === 'approved') return 'check'
  return 'arc'
}
const pieTrackColor = (item) => (pieMode(item) === 'arc' ? '#E9EBF0' : PROGRESS_GREEN)

/** 生成扇形 path（从 12 点方向顺时针填充） */
const piePath = (fraction, r = 11, cx = 12, cy = 12) => {
  if (fraction <= 0) return ''
  if (fraction >= 1) {
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`
  }
  const angle = fraction * 2 * Math.PI
  const x = cx + r * Math.sin(angle)
  const y = cy - r * Math.cos(angle)
  const large = fraction > 0.5 ? 1 : 0
  return `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${large} 1 ${x} ${y} Z`
}

/**
 * 详情跳转：
 * - 默认始终定位到「基本信息」步骤
 * - role=creator 标识申请者视角，详情页不显示「驳回 / 投运」按钮
 */
const handleEdit = (item) => {
  router.push({
    path: '/approve',
    query: {
      stationId: item.stationId,
      step: 'basic',
      role: 'creator',
    },
  })
}
</script>

<style scoped lang="scss">
.personal-stations {
  display: flex;
  flex-direction: column;
  width: calc(100vw - var(--sidebar-width, 154px));
  max-width: calc(100vw - var(--sidebar-width, 154px));
  height: calc(100vh - var(--header-height, 70px));
  min-width: 0;
  box-sizing: border-box;
  padding: clamp(8px, 1.2vw, 20px);
  font-family: 'HarmonyOS Sans SC', 'PingFang SC', sans-serif;
  overflow: hidden;
}

.ps-frame {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* 卡片内标题：一根竖线 + 主色 */
.ps-section-title {
  position: relative;
  font-size: 16px;
  font-weight: 700;
  color: #2e2e2e;
  margin-bottom: 14px;
  padding-left: 12px;
}

.ps-section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 16px;
  border-radius: 2px;
  background: #2655f1;
}

/* 工具栏 */
.toolbar-top {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toolbar-main {
  min-width: 0;
}

.action-btn {
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  .search-label {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
    text-align: right;
    min-width: 64px;
  }

  .search-input {
    width: 220px;
  }

  .search-input--select {
    width: 180px;
  }
}

.search-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
  row-gap: 12px;
}

/* 表格容器 */
.table-wrap {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ps-table-host {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ps-table {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
  min-height: 0;
}

.ps-table :deep(.v-table__wrapper) {
  overflow: auto;
}

.ps-table :deep(.v-table__wrapper > table) {
  width: 100%;
  min-width: max-content;
  table-layout: fixed;
}

.ps-table :deep(thead th) {
  background: rgba(255, 255, 255, 0.96) !important;
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.8) !important;
  white-space: nowrap;
}

.ps-table :deep(tbody td) {
  vertical-align: middle;
  color: #2e2e2e;
}

.ps-table :deep(.ps-table-row:hover)>td {
  background: rgba(38, 85, 241, 0.03);
}

/* 展开行 */
.ps-expand-cell {
  padding: 16px 16px !important;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

/* 操作列固定在右侧 */
.ps-table :deep(th.sticky-action-col),
.ps-table :deep(td.sticky-action-col) {
  position: sticky;
  right: 0;
  white-space: nowrap;
  box-sizing: border-box;
  border-left: 1px solid var(--color-td-border, rgba(0, 0, 0, 0.08));
  box-shadow: -8px 0 14px -10px rgba(0, 0, 0, 0.18);
}

.ps-table :deep(th.sticky-action-col) {
  z-index: 4;
  background-color: #fff !important;
}

.ps-table :deep(td.sticky-action-col) {
  z-index: 1;
  background-color: #fff;
}

.ps-table :deep(tbody tr:hover td.sticky-action-col) {
  background-color: #fff;
  background-image: linear-gradient(90deg, rgba(38, 85, 241, 0.06) 0%, rgba(85, 7, 253, 0.06) 100%);
}

/* 操作按钮组 */
.action-buttons {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.action-buttons :deep(.v-btn) {
  min-width: auto;
  padding-inline: 8px;
  white-space: nowrap;
}

/* 单元格文本 */
.cell-name {
  max-width: 280px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-text {
  font-size: 14px;
  color: #2e2e2e;
  line-height: 1.5;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-sub {
  font-size: 13px;
  line-height: 1.5;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-muted {
  color: rgba(46, 46, 46, 0.5);
}

.ps-link-color {
  color: #2655f1;
}

/* 审批状态 */
.ps-status {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ps-status-chip {
  height: 24px !important;
  font-size: 12px;
  font-weight: 500;
}

.ps-status-icon {
  flex-shrink: 0;
}

.ps-status-icon-btn {
  display: inline-flex;
  align-items: center;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: inherit;
  line-height: 0;
}

/* 驳回留言弹层 */
.ps-reject-pop {
  min-width: 240px;
  max-width: 340px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  padding: 14px 16px;
}

.ps-reject-pop-title {
  font-size: 14px;
  font-weight: 600;
  color: #2e2e2e;
  margin-bottom: 10px;
}

.ps-reject-msg+.ps-reject-msg {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.ps-reject-msg-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.ps-reject-approver {
  font-size: 13px;
  font-weight: 500;
  color: #2e2e2e;
}

.ps-reject-time {
  font-size: 12px;
  color: rgba(46, 46, 46, 0.45);
}

.ps-reject-content {
  font-size: 13px;
  line-height: 1.6;
  color: rgba(46, 46, 46, 0.75);
}

.ps-reject-empty {
  font-size: 13px;
  color: rgba(46, 46, 46, 0.45);
}

/* 配置进度 */
.ps-progress-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ps-progress-text {
  font-size: 13px;
  color: rgba(46, 46, 46, 0.7);
}

.ps-pie {
  display: inline-flex;
  line-height: 0;
}

/* 展开区：建站流程 */
.ps-steps {
  display: flex;
  align-items: flex-start;
  background: #f5f6f8;
  border-radius: 12px;
  padding: 22px 40px;
}

.ps-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  text-align: center;
}

.ps-step-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.ps-step-label {
  margin-top: 10px;
  font-size: 13px;
  color: #2e2e2e;
  white-space: nowrap;
}

.ps-step-state {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(46, 46, 46, 0.5);
  white-space: nowrap;
}

.ps-step-line {
  flex: 1 1 auto;
  height: 2px;
  min-width: 36px;
  margin-top: 11px;
}

/* 空态 */
.empty-tip {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(46, 46, 46, 0.45);
  font-size: 14px;
}

/* 分页 */
.ps-pagination {
  :deep(.table-pagination) {
    padding-left: 0;
    padding-right: 0;
  }
}

@media (max-width: 1200px) {
  .ps-steps {
    padding: 18px 16px;
    overflow-x: auto;
  }
}

/* ============ 小屏 1366x768 兼容适配（对齐电站管理列表） ============ */
@media (max-width: 1440px) {
  .toolbar-main {
    gap: 12px !important;
    row-gap: 10px !important;
  }

  .search-item {
    gap: 8px;

    .search-label {
      min-width: auto;
      font-size: 13px;
    }

    .search-input {
      width: 180px;
    }

    .search-input--select {
      width: 140px;
    }
  }

  .search-actions {
    gap: 8px !important;
    row-gap: 8px !important;
  }

  .action-btn {
    padding: 0 12px !important;
    font-size: 13px !important;
    letter-spacing: 0 !important;
    min-width: 0 !important;
  }

  .ps-table :deep(th.sticky-action-col),
  .ps-table :deep(td.sticky-action-col) {
    width: 280px;
    min-width: 280px;
  }
}

@media (max-width: 1366px) {
  .toolbar-main {
    gap: 10px !important;
    row-gap: 8px !important;
  }

  .search-item {
    gap: 6px;

    .search-label {
      font-size: 12px;
    }

    .search-input {
      width: 160px;
    }

    .search-input--select {
      width: 128px;
    }
  }

  .search-actions {
    gap: 6px !important;
    row-gap: 8px !important;
  }

  .action-btn {
    padding: 0 10px !important;
    font-size: 12px !important;
    height: 32px !important;
  }

  .action-btn :deep(.v-icon) {
    font-size: 16px !important;
  }

  .ps-frame {
    padding: 12px !important;
  }

  .ps-table :deep(th.sticky-action-col),
  .ps-table :deep(td.sticky-action-col) {
    width: 280px;
    min-width: 280px;
  }

  .ps-table :deep(thead th),
  .ps-table :deep(tbody td) {
    padding-inline: 12px !important;
  }

  .cell-name {
    max-width: 220px;
  }

  .action-buttons :deep(.v-btn) {
    padding-inline: 6px !important;
    font-size: 12px !important;
  }
}

@media (max-height: 800px) {
  .toolbar-top {
    margin-bottom: 12px !important;
  }

  .ps-pagination {
    :deep(.table-pagination) {
      padding-top: 8px;
      padding-bottom: 4px;
    }
  }
}
</style>
